import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { pageTitle } from '../../styles/pageStyles';
import useDocumentTitle from '../../components/auth/useDocumentTitle';
import PublicationLayout from '../../components/publication-layout/PublicationLayout';
import BankBalance from './BankBalance';
import { fetchInvoiceList, InvoiceListResponse, InvoiceListItem } from './api';

// ── Styled ─────────────────────────────────────────────────────────────────

const Main = styled.div``;

const PageTitle = styled.h1`
  ${pageTitle}
  margin: 0 0 24px 0;
`;

const Banner = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;

  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const TableContainer = styled.div`
  flex: 1;
`;

const TableHeader = styled.div`
  display: flex;
  background: #f5f5f5;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
`;

const ColPeriod = styled.div`flex: 1;`;
const ColTxn = styled.div`width: 120px; text-align: center;`;

const TableRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;

  &:nth-child(even) { background: #fafafa; }

  a { color: #0094ab; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const Pagination = styled.nav`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 16px;
  font-size: 14px;

  a {
    color: #0094ab;
    text-decoration: none;
    padding: 4px 8px;
    border-radius: 3px;
    &:hover { background: #e7f5f8; }
  }

  span {
    padding: 4px 8px;
    border-radius: 3px;
    background: #0094ab;
    color: white;
  }
`;

const EmptyState = styled.p`
  color: #666;
  font-size: 14px;
  padding: 20px 0;
`;

// ── Component ──────────────────────────────────────────────────────────────

function FinancialActivityPage() {
  const { publicationId } = useParams<{ publicationId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<InvoiceListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useDocumentTitle('Financial activity');

  const load = useCallback(
    (p: number) => {
      if (!publicationId) return;
      setError(null);
      fetchInvoiceList(publicationId, p).then(setData).catch(() => {
        setError('Unable to load financial activity. Please refresh the page.');
      });
    },
    [publicationId]
  );

  useEffect(() => { load(page); }, [load, page]);

  if (!publicationId) return null;

  const renderRow = (invoice: InvoiceListItem) => (
    <TableRow key={invoice.id}>
      <ColPeriod>
        <a href={`/publications/${publicationId}/invoices/${invoice.id}`}>
          {invoice.title}
        </a>
      </ColPeriod>
      <ColTxn>{invoice.transaction_count}</ColTxn>
    </TableRow>
  );

  const hasAny = data && (data.current_invoice || data.invoices.length > 0);

  return (
    <PublicationLayout>
      <Main>
        {data?.publication_inactive && (
          <Banner>
            Heads up, this publication has been deactivated. But, you can still view old statements.
            Please reach out to your account manager if you have questions.
          </Banner>
        )}

        <PageTitle>Financial activity</PageTitle>

        {data === null && !error && <p>Loading...</p>}
        {error && <p style={{ color: '#c0392b', padding: '20px 0', fontSize: '14px' }}>{error}</p>}

        {data !== null && !hasAny && (
          <EmptyState>No statements found</EmptyState>
        )}

        {data !== null && hasAny && (
          <TwoColumn>
            <BankBalance publicationId={publicationId} />

            <TableContainer>
              <TableHeader>
                <ColPeriod>Period</ColPeriod>
                <ColTxn>Transactions</ColTxn>
              </TableHeader>

              {data.current_invoice && renderRow(data.current_invoice)}
              {data.invoices.map(renderRow)}

              {data.total_pages > 1 && (
                <Pagination aria-label="Invoice pages">
                  {page > 1 && (
                    <a href="#" onClick={e => { e.preventDefault(); setPage(p => p - 1); }}>
                      ‹ Prev
                    </a>
                  )}
                  {Array.from({ length: data.total_pages }, (_, i) => i + 1).map(p =>
                    p === page ? (
                      <span key={p}>{p}</span>
                    ) : (
                      <a key={p} href="#" onClick={e => { e.preventDefault(); setPage(p); }}>
                        {p}
                      </a>
                    )
                  )}
                  {page < data.total_pages && (
                    <a href="#" onClick={e => { e.preventDefault(); setPage(p => p + 1); }}>
                      Next ›
                    </a>
                  )}
                </Pagination>
              )}
            </TableContainer>
          </TwoColumn>
        )}
      </Main>
    </PublicationLayout>
  );
}

export default FinancialActivityPage;
